/**
 * Light/dark theme. Everyone starts in light; a visitor who picks dark keeps it
 * on this device (localStorage). The theme is set as data-theme on <html>
 * (index.html does it before first paint too). Charts read colours from CSS
 * variables, so they follow the theme via `useResolvedTheme`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemeChoice = "light" | "dark";
export type ResolvedTheme = ThemeChoice;

const KEY = "rai.theme";

interface ThemeContextValue {
  choice: ThemeChoice;
  resolved: ResolvedTheme;
  setChoice: (c: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Light unless dark was saved (an old "system" choice also means light). */
function readChoice(): ThemeChoice {
  try {
    return localStorage.getItem(KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readChoice);

  // A layout effect, so the new data-theme is in place before any chart's
  // (passive) effect reads the colour tokens.
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = choice;
    document.documentElement.style.colorScheme = choice;
  }, [choice]);

  const setChoice = useCallback((c: ThemeChoice) => {
    setChoiceState(c);
    try {
      localStorage.setItem(KEY, c);
    } catch {
      // storage blocked: the choice lasts for this visit
    }
  }, []);

  const value = useMemo(() => ({ choice, resolved: choice, setChoice }), [choice, setChoice]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme outside ThemeProvider");
  return ctx;
}

export function useResolvedTheme(): ResolvedTheme {
  return useContext(ThemeContext)?.resolved ?? "light";
}

/** Read a design token (CSS custom property) as a colour string. */
export function cssVar(name: string, el: Element = document.documentElement): string {
  return getComputedStyle(el).getPropertyValue(name).trim();
}
